/**
 * DragScroll — makes a horizontally overflowing row reachable with a mouse.
 *
 * `overflow-x: auto` is enough on a phone, where a swipe scrolls it natively.
 * On a desktop it is a dead end: a wheel only scrolls vertically, there is no
 * drag-to-scroll, and the scrollbar is hidden by the shell's native-feel rule
 * (`.ga-root *::-webkit-scrollbar`). Anything past the right edge — the third
 * category chip, the far end of a card rail — simply cannot be reached.
 *
 * So this adds the two gestures a mouse actually has:
 *   • a vertical wheel scrolls the row sideways, but only while the row has
 *     somewhere left to go — at either end the event is left alone so the page
 *     behind it keeps scrolling instead of the wheel dying in a chip row;
 *   • press and drag pans it, with a few pixels of slack before a press turns
 *     into a drag, and the click that ends a drag swallowed so panning past a
 *     chip does not also select it.
 *
 * Touch is deliberately untouched: the browser's own inertial scrolling is
 * better than anything reimplemented here, and hijacking pointer events would
 * replace it with something worse.
 *
 * Listeners are registered through the owning State, so the framework disposes
 * them when the screen exits.
 */
export class DragScroll
{
    /** How far a press must travel before it counts as a drag, in pixels. */
    static THRESHOLD = 4;

    /**
     * @param {IF.State} state Owns the listeners (tracked cleanup on exit)
     * @param {HTMLElement} element The scroll container
     * @param {Object} [options]
     * @param {boolean} [options.wheel=true] Translate a vertical wheel into
     *        sideways scrolling. Right for a row on a screen that does not
     *        scroll vertically (the category chips); WRONG for a row sitting in
     *        the middle of a long page, where it would stop the page dead every
     *        time the pointer happened to cross a strip of photos. Rails inside
     *        a scrolling detail screen therefore pass false and are panned by
     *        dragging, which is what a carousel does everywhere else on the web.
     */
    static attach( state, element, { wheel = true } = {} )
    {
        if ( !element )
        {
            return;
        }

        element.classList.add( "is-dragscroll" );

        let pointerId = null;
        let startX = 0;
        let startScroll = 0;
        let dragged = false;

        state.addEventListener( element, "pointerdown", ( event ) =>
        {
            // Touch and pen scroll natively and far better.
            if ( event.pointerType !== "mouse" || event.button !== 0 )
            {
                return;
            }

            pointerId = event.pointerId;
            startX = event.clientX;
            startScroll = element.scrollLeft;
            dragged = false;
        } );

        state.addEventListener( element, "pointermove", ( event ) =>
        {
            if ( pointerId === null || event.pointerId !== pointerId )
            {
                return;
            }

            const dx = event.clientX - startX;

            if ( !dragged )
            {
                if ( Math.abs( dx ) < DragScroll.THRESHOLD )
                {
                    return;
                }
                dragged = true;
                // Capture only once it IS a drag: taking the pointer on
                // mousedown would break a plain click on a chip.
                element.setPointerCapture( pointerId );
                element.classList.add( "is-dragging" );
            }

            element.scrollLeft = startScroll - dx;
            event.preventDefault();
        } );

        const release = () =>
        {
            if ( pointerId !== null && element.hasPointerCapture( pointerId ) )
            {
                element.releasePointerCapture( pointerId );
            }
            pointerId = null;
            element.classList.remove( "is-dragging" );
        };

        state.addEventListener( element, "pointerup", release );
        state.addEventListener( element, "pointercancel", release );

        // Capture phase, so the chip's own click handler never runs: a drag
        // that happens to end on "Touren" must not switch to Touren.
        state.addEventListener( element, "click", ( event ) =>
        {
            if ( !dragged )
            {
                return;
            }
            dragged = false;
            event.stopPropagation();
            event.preventDefault();
        }, true );

        if ( !wheel )
        {
            return;
        }

        state.addEventListener( element, "wheel", ( event ) =>
        {
            // A trackpad sends a real horizontal delta; leave that alone.
            if ( event.deltaX !== 0 )
            {
                return;
            }

            const max = element.scrollWidth - element.clientWidth;
            if ( max <= 0 )
            {
                return;
            }

            // At either end, hand the wheel back to the page.
            if ( ( event.deltaY < 0 && element.scrollLeft <= 0 ) ||
                 ( event.deltaY > 0 && element.scrollLeft >= max ) )
            {
                return;
            }

            element.scrollLeft += event.deltaY;
            event.preventDefault();
        }, { passive : false } );
    }

    /**
     * Scroll `child` into view inside `element`, moving nothing else.
     *
     * `scrollIntoView()` would do this and also scroll every ancestor, which
     * on a screen whose list is its own scroll container means the page jumps.
     *
     * @param {number} [margin=22] Breathing room to leave at the edge, matching
     *        the screen's own gutter.
     */
    static reveal( element, child, margin = 22 )
    {
        if ( !element || !child )
        {
            return;
        }

        const row = element.getBoundingClientRect();
        const box = child.getBoundingClientRect();

        if ( box.right > row.right - margin )
        {
            element.scrollLeft += box.right - row.right + margin;
        }
        else if ( box.left < row.left + margin )
        {
            element.scrollLeft -= row.left + margin - box.left;
        }
    }
}
